import { RESTClient, RESTClientMock } from '@api/client';
import { API_URL } from '@api/apiDictionary';
import { fetchUploadFormMock } from '@api/mocks/uploadForm';
import { USE_API_MOCKS } from '../env';
import axios, { AxiosRequestConfig } from 'axios';

import { uploadVisitMock } from '@api/mocks/uploadVisit';
import { uploadStatusCodeMock } from '@api/mocks/uploadStatusCode';
import { uploadSignedUrlMock } from '@api/mocks/uploadSugnedUrl';
import {
  SignedUrlType,
  UploadFormData,
} from '@components/SiteUserUpload/types/uploadTypes';
import {
  PolingResponse,
  VerificationStepsItem,
  VisitResponse,
  CreateVisit,
} from 'src/store-redux/siteUpload/siteUploadStore.types';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';

const api = USE_API_MOCKS ? RESTClientMock : RESTClient;

export const fetchUploadForm = async (
  clinicalTrialId: string,
  dataType: UploadFileTypeEnum,
  siteId: string,
  visitDefinitionId?: string | null,
) => {
  if (USE_API_MOCKS) {
    fetchUploadFormMock();
  }

  return api.get<UploadFormData[]>(API_URL.uploadInventoryQuestionnaires, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
      'x-dynamika-site-id': siteId,
    },
    params: {
      dataType,
      ...(visitDefinitionId && { visitDefinitionId }),
    },
  });
};

export const getSignedUrl = async (
  uploadId: string,
  clinicalTrialId: string,
  siteId: string
) => {
  if (USE_API_MOCKS) {
    return uploadSignedUrlMock();
  }

  return api.get<SignedUrlType>(
    API_URL.uploadItemsUrl.replace(':uploadId', uploadId),
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
        'x-dynamika-site-id': siteId,
      },
    }
  );
};

interface UploadOptions extends AxiosRequestConfig {
  signal?: AbortSignal;
}

export const setUploadFile = async (
  file: File,
  signedUrl: string,
  contentType?: string,
  options: UploadOptions = {}
) => {
  if (USE_API_MOCKS) {
    return uploadStatusCodeMock();
  }
  //no response
  const response = await axios.request({
    method: 'PUT',
    url: signedUrl,
    data: file,
    headers: {
      'Content-Type': contentType ?? 'application/zip',
    },
    ...options,
  });

  return { code: response.status };
};

export const setUploadFinalize = async (
  uploadId: string,
  clinicalTrialId: string,
  siteId: string,
  additionalComment: string | undefined
) => {
  if (USE_API_MOCKS) {
    return uploadStatusCodeMock();
  }
  //no response
  const response = await api.put(
    `${API_URL.uploadItems}/${uploadId}/finalize`,
    { comment: additionalComment },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
        'x-dynamika-site-id': siteId,
      },
    }
  );

  return { code: response.status };
};

export const submitUpload = async (
  uploadId: string,
  clinicalTrialId: string,
  siteId: string,
  verificationStepsData: Partial<VerificationStepsItem>[]
) => {
  //no response
  const response = await api.put(
    `${API_URL.uploadItems}/${uploadId}/confirm`,
    { verificationSteps: verificationStepsData },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
        'x-dynamika-site-id': siteId,
      },
    }
  );

  return { code: response.status };
};

export const getPolingStatus = async (
  uploadId: string,
  clinicalTrialId: string,
  siteId: string
): Promise<{ data: PolingResponse }> => {
  return await api.get<PolingResponse>(
    `${API_URL.uploadItems}/${uploadId}/status`,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
        'x-dynamika-site-id': siteId,
      },
    }
  );
};

type UpdateUploadFormArgs = {
  data: object;
  uploadId: string;
  questionnaireId: string;
  clinicalTrialId: string;
  siteId: string;
};
export const updateUploadForm = async (args: UpdateUploadFormArgs) => {
  if (USE_API_MOCKS) {
    return uploadStatusCodeMock();
  }
  return api.put(
    `${API_URL.uploadItems}/${args.uploadId}/questionnaire`,
    {
      questionnaireId: args.questionnaireId,
      response: args.data,
    },
    {
      headers: {
        'x-dynamika-clinical-trial-id': args.clinicalTrialId,
        'x-dynamika-site-id': args.siteId,
      },
    }
  );
};

export const uploadVisit = async (
  data: CreateVisit,
  clinicalTrialId: string,
  siteId: string
): Promise<{ data: VisitResponse }> => {
  if (USE_API_MOCKS) {
    uploadVisitMock({
      data,
      clinicalTrialId,
      siteId,
    });
  }
  return RESTClient.post<VisitResponse>(
    API_URL.uploadItems,
    { ...data },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
        'x-dynamika-site-id': siteId,
      },
    }
  );
};

export const cancelUpload = async (
  uploadId: string,
  clinicalTrialId: string,
  siteId: string
): Promise<void> => {
  return RESTClient.delete(`${API_URL.uploadItems}/${uploadId}/cancel`, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
      'x-dynamika-site-id': siteId,
    },
  });
};
